import { createSlice } from '@reduxjs/toolkit';
import Response from 'app/config/Response';
import app from 'app/config/app';
import isEmpty from 'helpers/isEmpty';

const initialState = {
  index: {
    data: [],
    params: {
      page_size: app.perPage || 10,
      page_number: 1,
      sort_by: 'created_ts',
      order_by: 'desc',
    },

    isLoading: false,
    totalCount: 0,
    totalPage: 0,
    isError: false,
    errorCode: null,
    errorMessage: null,
  },

  create: {
    showProcessingModal: false,
    isCreating: false,
    isCreated: false,
    isFailed: false,
    errors: null,
    data: null,
    retries: 0,
    errorMessage: null,
  },

  update: {
    showProcessingModal: false,
    isUpdating: false,
    isUpdated: false,
    isFailed: false,
    errors: null,
    data: null,
    retries: 0,
    errorMessage: null,
  },

  show: {
    showBriefDetailModal: false,
    licensingPartnerId: null,
    isLoading: false,
    isError: false,
    errorCode: null,
    errorMessage: null,
    data: null,
  },
};

export const licensingPartnerSlice = createSlice({
  name: 'licensingPartners',
  initialState,
  reducers: {
    getLicensingPartnersInit: (state) => {
      state.index = {
        ...state.index,
        isLoading: true,
        isError: false,
        errorCode: null,
        errorMessage: null,
      };
    },

    getLicensingPartnersSucess: (state, { payload }) => {
      let { data, pagination } = payload;

      const { totalCount, totalPage, currentPage, pageSize } = pagination;

      let startSN = (currentPage - 1) * pageSize;

      data = data?.map((item) => ({ sn: (startSN += 1), ...item }));

      state.index = {
        ...state.index,
        isLoading: false,
        isError: false,
        errorCode: null,
        errorMessage: null,
        data,
        totalCount,
        totalPage,
      };
    },

    getLicensingPartnersFailure: (state, { payload }) => {
      state.index = {
        ...state.index,
        isLoading: false,
        isError: true,
        errorCode: payload?.response?.status ?? 0,
        errorMessage: payload?.response?.message,
      };
    },

    resetGetLicensingPartnersFailure: (state) => {
      state.index = {
        ...state.index,
        isLoading: false,
        isError: false,
        errorCode: null,
        errorMessage: null,
      };
    },

    getLicensingPartnerInit: (state) => {
      state.show = {
        ...state.show,
        isLoading: true,
        isError: false,
        errorCode: null,
        errorMessage: null,
        data: null,
      };
    },

    getLicensingPartnerSuccess: (state, { payload }) => {
      if (isEmpty(payload?.data)) {
        state.show = {
          ...state.show,
          isLoading: false,
          isError: true,
          errorCode: Response.HTTP_NOT_FOUND,
          errorMessage: 'No Licensing not found',
        };
      } else {
        state.show = {
          ...state.show,
          isLoading: false,
          isError: false,
          errorCode: null,
          errorMessage: null,
          data: payload.data,
        };
      }
    },

    getLicensingPartnerFailure: (state, { payload }) => {
      state.show = {
        ...state.show,
        isLoading: false,
        isError: true,
        errorCode: payload?.response?.status ?? 0,
        errorMessage: payload?.response?.message,
        data: null,
      };
    },

    resetGetLicensingPartnerFailure: (state) => {
      state.show = {
        ...state.show,
        isLoading: false,
        isError: false,
        errorCode: null,
        errorMessage: null,
        data: null,
      };
    },

    addLicensingPartnerInit: (state) => {
      state.create = {
        ...state.create,
        showProcessingModal: true,
        isCreating: true,
        isCreated: false,
        isFailed: false,
        errors: null,
        data: null,
      };
    },

    addLicensingPartnerSuccess: (state, { payload }) => {
      state.create = {
        ...state.create,
        showProcessingModal: true,
        isCreating: false,
        isCreated: true,
        isFailed: false,
        errors: null,
        data: payload.data,
      };
    },

    addLicensingPartnerFailure: (state, { payload }) => {
      state.create = {
        ...state.create,
        showProcessingModal: true,
        isCreating: false,
        isCreated: false,
        isFailed: true,
        errors: null,
        data: null,
        errorMessage: payload?.response?.message || '',
      };
    },

    addLicensingPartnerReset: (state) => {
      state.create = {
        showProcessingModal: false,
        isCreating: false,
        isCreated: false,
        isFailed: false,
        errors: null,
        data: null,
        retries: 0,
        errorMessage: null,
      };
    },

    retryToCreate: (state) => {
      state.create.retries += 1;
    },

    setShowCreateProcessingModal: (state, { payload }) => {
      state.create.showProcessingModal = payload;
    },

    updateLicensingPartnerInit: (state) => {
      state.update = {
        ...state.update,
        showProcessingModal: true,
        isUpdating: true,
        isUpdated: false,
        isFailed: false,
        errors: null,
        data: null,
      };
    },

    updateLicensingPartnerSuccess: (state, { payload }) => {
      state.update = {
        ...state.update,
        showProcessingModal: true,
        isUpdating: false,
        isUpdated: true,
        isFailed: false,
        errors: null,
        data: payload.data,
      };
    },

    updateLicensingPartnerFailure: (state, { payload }) => {
      state.update = {
        ...state.update,
        showProcessingModal: true,
        isUpdating: false,
        isUpdated: false,
        isFailed: true,
        errors: null,
        data: null,
        errorMessage: payload?.response?.message || '',
      };
    },

    updateLicensingPartnerReset: (state) => {
      state.update = {
        showProcessingModal: false,
        isUpdating: false,
        isUpdated: false,
        isFailed: false,
        errors: null,
        data: null,
        retries: 0,
        errorMessage: null,
      };
    },

    deleteLicensingPartnerInit: (state, { payload: licensingPartnerId }) => {
      state.index.data = state.index.data.map((item) => {
        if (item.id === licensingPartnerId) {
          return {
            ...item,
            isDeleting: true,
          };
        }
        return item;
      });
    },

    // licensingPartnerId

    deleteLicensingPartnerSuccess: (state, { payload: licensingPartnerId }) => {
      const { page_number: currentPage, page_size: pageSize } = state.index.params;

      let startSN = (currentPage - 1) * pageSize;

      let data = state.index.data.filter((item) => item.id !== licensingPartnerId);

      state.index.data = data.map((item) => ({ ...item, sn: (startSN += 1) }));
    },

    deleteLicensingPartnerFailure: (state, { payload: licensingPartnerId }) => {
      state.index.data = state.index.data.map((item) => {
        if (item.id == licensingPartnerId) {
          return {
            ...item,
            isDeleting: false,
          };
        }
        return item;
      });
    },

    retryToUpdate: (state) => {
      state.update.retries += 1;
    },

    setShowEditProcessingModal: (state, { payload }) => {
      state.update.showProcessingModal = payload;
    },

    setParam: (state, { payload }) => {
      state.index.params = {
        ...state.index.params,
        ...payload,
      };
    },

    resetParam: (state) => {
      state.index.params = {
        page_size: state.index.params.page_size,
        page_number: 1,
      };
    },

    deleteFilterParams: (state, { payload }) => {
      if (Array.isArray(payload)) payload.forEach((key) => delete state.index.params[key]);
      else delete state.index.params[payload];
    },

    getLicensingPartnerBriefDetailsInit: (state, { payload }) => {
      state.show = {
        ...state.show,
        showBriefDetailModal: payload.showBriefDetailModal,
        licensingPartnerId: payload.licensingPartnerId,
      };
    },

    hideLicensingPartnerBriefDetails: (state) => {
      state.show = {
        showBriefDetailModal: false,
        licensingPartnerId: null,
        isLoading: false,
        isError: false,
        errorCode: null,
        errorMessage: null,
        data: null,
      };
    },
  },
});

export const {
  setParam,
  resetParam,
  retryToCreate,
  retryToUpdate,
  deleteFilterParams,
  getLicensingPartnerInit,
  getLicensingPartnerSuccess,
  getLicensingPartnerFailure,
  getLicensingPartnersFailure,
  getLicensingPartnersSucess,
  addLicensingPartnerInit,
  addLicensingPartnerFailure,
  addLicensingPartnerSuccess,
  addLicensingPartnerReset,
  getLicensingPartnersInit,
  updateLicensingPartnerInit,
  setShowCreateProcessingModal,
  setShowEditProcessingModal,
  updateLicensingPartnerSuccess,
  updateLicensingPartnerFailure,
  updateLicensingPartnerReset,
  resetGetLicensingPartnerFailure,
  resetGetLicensingPartnersFailure,
  hideLicensingPartnerBriefDetails,
  getLicensingPartnerBriefDetailsInit,
  deleteLicensingPartnerInit,
  deleteLicensingPartnerSuccess,
  deleteLicensingPartnerFailure,
} = licensingPartnerSlice.actions;

export default licensingPartnerSlice.reducer;
